import {Inject, Injectable} from '@angular/core';
import {Customer, H7Agreement, TopLevelHierarchy} from 'src/app/global/ebr-models';
import {BehaviorSubject, Observable} from 'rxjs';
import {TimePeriod,Filters, FilterSet, FrsFilterSet, InstitutionalFilterSet, PestFilterSet, QsrFilterSet, DistributorFilterSet, OneCustomerFilterSet} from '../../global/filter-model'
import {FilterOptionService} from "../../features/filter-selection/services/filter-option.service";
import {CountriesService} from "./countries.service";
import {FilterType} from "../../features/filter-panel/types/filters";
import { DistributorFilterModel, FilterOption, DivDataView, ContractFilterOptionsModel, QSRFilterOptionsModel, PestFilterModel, FRSFilterOptionsModel, CustomDateRange } from 'src/app/global/filter-options-types';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { forEach, cloneDeep } from 'lodash';
import { Moment } from 'moment';
import * as _moment from 'moment';
import { AdvancedOneCustomerFilters } from 'src/app/config/GlobalCacheLoader.service';
import { Division } from './ebr-api-client/ebr-api-client.g';
import { FilterRequests } from './filter-requests.service';
import { FEATURE_FLAGS, FeatureFlags } from 'src/app/config/AppInitializer.factory';
import { FiltersStore } from '../stores/filters.store';

@Injectable({
  providedIn: 'root'
})
export class FiltersService {

  selectedDataView:string = "";
  selectedDivision:string = "";

  public selectedFilters = new BehaviorSubject<ContractModel|undefined>(undefined);
  selectedFiltersObservable = this.selectedFilters.asObservable();

  /** @deprecated Use FiltersStore.filterSet$ instead */
  public filterSet = new BehaviorSubject<FilterSet|undefined>(undefined);
  /** @deprecated Use FiltersStore.filterSet$ instead */
  filterSetObservable = this.filterSet.asObservable();

  // OneCustomer name for 1C Integration
  /**
   * @deprecated Use FiltersStore.oneCustomerNameValue instead
   */
  get oneCustomerName() {
    return this.filtersStore.oneCustomerNameValue;
  }

  /**
   * @deprecated Use FiltersStore.oneCustomerName$ instead
   */
  get oneCustomerNameObservable() {
    return this.filtersStore.oneCustomerNameObservable;
  }

  // Additional Filters for 1C Integration
  oneCustomerFilters = new BehaviorSubject<AdvancedOneCustomerFilters|undefined>(undefined);
  oneCustomerFiltersObservable = this.oneCustomerFilters.asObservable();

  private currentDate = new Date();
  //Default to the first day of the past month
  private defaultStartDate : Date = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth()-1, 1)
  //Default to the last day of the previous month
  private defaultEndDate : Date = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 0);
  // 36 months prior
  private minimumDate : Date = new Date(this.currentDate.getFullYear()-3, this.currentDate.getMonth(), 1);
  // Previous Month, setting the day to zero makes the maximumDate rollback into the previous month
  private maximumDate : Date = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 0);

  // Custom Date Ranges
  customDateRange = new BehaviorSubject<CustomDateRange>
    ({startDate: {moment: _moment.default(this.defaultStartDate), formatted:_moment.default(this.defaultStartDate).format('YYYY-MM')},
      endDate: {moment: _moment.default(this.defaultEndDate), formatted:_moment.default(this.defaultEndDate).format('YYYY-MM')}});

  customDateRangeObservable = this.customDateRange.asObservable();

  // Selection Behavior Source
  private filtersSource = new BehaviorSubject<Filters>(new Filters());
  private distributorFilterSource = new BehaviorSubject<ContractModel|undefined>(undefined);

  // Confirmed Selections
  filtersConfirmed = this.filtersSource.asObservable();

  private selectedAccountNosSubject = new BehaviorSubject<string[]>([]);
  selectedAccountNosConfirmed = this.selectedAccountNosSubject.asObservable();

  //Benchmarking Selections
  private benchMarkOptionsSubject = new BehaviorSubject<string[]>([]);
  benchMarkOptionsObservable = this.benchMarkOptionsSubject.asObservable();

  // Pre-Submission Selection
  regionSelected = '';
  countriesSelected: string[] = [];
  topLevelHierarchiesSelected: TopLevelHierarchy[] = [];
  h7AgreementsSelected: H7Agreement[] = [];
  customersSelected: Customer[] = [];
  customersNumNameSelected: Customer[] = [];
  periodSelected = '';
  templateSelected = '';
  templateTranslationSelected = '';

  // account selections
  accts : string[] = [];

  // First load observable to Neptune UI
  firstFilterLoadComplete = new BehaviorSubject<boolean>(false);
  firstFilterLoadCompleteObservable = this.firstFilterLoadComplete.asObservable();

  excludeDeletedAccounts: boolean = false;

  constructor(
    private filterOptionService: FilterOptionService,
    private countriesService: CountriesService,
    private filterRequests: FilterRequests,
    private filtersStore: FiltersStore,
    @Inject(FEATURE_FLAGS) private featureFlags: FeatureFlags) {
    this.filterOptionService.selectedDivisionDataView.subscribe(
      (dataView: DivDataView) => {
        this.selectedDataView = dataView.dataView;
        this.selectedDivision = dataView.division;
      }
    );
    this.filterRequests.excludeDeletedAccountsObservable.subscribe(f => this.excludeDeletedAccounts = f);
    this.selectedFiltersObservable.subscribe(filters => this.filtersStore.setSelectedFilters(filters));
    this.filterSetObservable.subscribe(fs => this.filtersStore.setFilterSet(fs));
  }

  setFirstLoadComplete(value: boolean) {
    this.firstFilterLoadComplete.next(value);
  }

  getFirstLoadComplete(){
    return this.firstFilterLoadComplete.value;
  }

  setOneCustomerName(name: string): void {
    this.filtersStore.setOneCustomerName(name);
  }

  clearOneCustomerName(): void {
    this.filtersStore.clearOneCustomerName();
  }

  getDateRangeForMixpanel(dateRangeSelected:string){
    let customDateRange ='';
    if(dateRangeSelected === 'Custom'){
      this.customDateRangeObservable.subscribe(f => customDateRange = `${f.startDate.formatted}:${f.endDate.formatted}`);
      return customDateRange;
    }
    else{
      return customDateRange;
    }
  }

  rememberPriorChoices(title:string, data:string) {
    localStorage.setItem(title, data);
  }

  forgetRecalledChoices(divDataView:string) {
    localStorage.removeItem('ebr'+divDataView+'Region');
    localStorage.removeItem('ebr'+divDataView+'Countries');
    localStorage.removeItem('ebr'+divDataView+'DateRange');
    localStorage.removeItem('ebr'+divDataView+'Agreement(H7)');
    localStorage.removeItem('ebr'+divDataView+'Templates');
    localStorage.removeItem('ebr'+divDataView+'TopLevelHierarchy(H5)');
    localStorage.removeItem('ebr'+divDataView+'CustomerNumber/Name(H5)');
    localStorage.removeItem('ebr'+divDataView+'AccountNumbers');
  }

  resetFilterValues() {
    // Reset selected filters
    var division = this.selectedDivision;
    var dataView = this.selectedDataView;

    // Set excluded deleted accounts based on division
    division == Division.Distributor || division == Division.Pest ? this.filterRequests.setExcludeDeletedAccounts(false) : this.filterRequests.setExcludeDeletedAccounts(true);

    var contractModel: ContractModel = {
      division: division,
      dataView: dataView,
      topLevelContractGroup: [],
      secondLevelContractFilter: [],
      thirdLevelContractFilter: [],
      fourthLevelContractFilter: [],
      accts: [],
      regions: [],
      countries: [],
      period: '',
      presentationType: '',
      translation: '',
      dateRange: '',
      excludeDeletedAccounts: this.excludeDeletedAccounts
    }
    // Reset filterSet subject using createFilterSet
    const base: Partial<FilterSet> = {
      Period: this.buildPeriod(''),
      Regions: [],
      Countries: []
    };
    this.createFilterSet('FilterSet', base, {});
    this.filterSet.next(undefined);
    this.selectedFilters.next(contractModel);
  }

  getH7NumbersStringed(): string{
    let stringedH7Nums = '';
    for (let v in this.h7AgreementsSelected){
      stringedH7Nums += this.h7AgreementsSelected[v].number + '|'
    }
    return stringedH7Nums;
  }

  async confirmSelectionsForOneCustomer(filters: Filters, primaryDivision:string) {
    switch(primaryDivision.toLowerCase()) {
      case 'institutional':
        this.selectedDivision = "Institutional";
        this.selectedDataView = "Contract";
        break;
      case 'qsr':
        this.selectedDivision = "QSR";
        this.selectedDataView = "";
        break;
      case 'frs':
        this.selectedDivision = "FRS";
        this.selectedDataView = "";
        break;
      case 'pest':
        this.selectedDivision = "Pest";
        this.selectedDataView = "";
        break;
      default:
        this.selectedDivision = 'Institutional'
        this.selectedDataView = "Contract";
        break;
    };

    // Set exclude deleted accounts observable to true
    this.filterRequests.setExcludeDeletedAccounts(true);

    //setting this so that if the user navigates to /filter page, it will default to their selected division
    this.filterOptionService.setSelectedDivisionDataView({division: this.selectedDivision, dataView: this.selectedDataView} as DivDataView);

    this.filtersSource.next(filters);

    // Populate filterSet as a OneCustomerFilterSet so endpoints that key off it (e.g. metric-counts-summary) work for 1C-redirected users too.
    const customerFilters = this.oneCustomerFilters.getValue();
    const base: Partial<FilterSet> = {
      Period: this.buildPeriod(filters.period),
      Regions: filters.regions ?? [],
      Countries: filters.countries ?? []
    };
    const specific: Partial<OneCustomerFilterSet> = {
      Division: this.selectedDivision,
      CustomerName: this.oneCustomerName,
      OperatingType: customerFilters?.operatingType,
      OperatingSubType: customerFilters?.operatingSubType,
      CustomerGroup: customerFilters?.customerGroup,
      EcolabGroup: customerFilters?.ecolabGroup,
      CustomerIndustry: customerFilters?.customerIndustry,
      Campuses: customerFilters?.campuses,
      Sites: customerFilters?.sites,
      ExcludeDeletedAccounts: true
    };
    this.createFilterSet('OneCustomerFilterSet', base, specific);

    this.selectedFilters.next(await this.getSelectedFilters());
  }

  async confirmSelectionsForContractView(filterOptions: FilterOption[],accts: string[]) {
    let filters: Filters = new Filters();
    if (filterOptions == undefined) {
      this.filtersSource.next(filters);
      this.selectedFilters.next(await this.getSelectedFilters());
      return;
    }
    const regions = this.filterOptionService.mapRegionsToCodes(filterOptions[ContractFilterOptionsModel.Regions].selected);
    const countries = this.countriesService.mapCountryNamesToCodes(filterOptions[ContractFilterOptionsModel.Countries].selected);
    const period = this.filterOptionService.mapNameToCode(filterOptions[ContractFilterOptionsModel.DateRange].selected.toString(), FilterType.PERIOD);
    const accountNumbers = [
      ...filterOptions[ContractFilterOptionsModel.CustomerNumberName].selected
        .map(str =>str.split(" ")[0]?.trim()),
        ...accts.map(acct => acct?.trim())
    ];

    filters = new Filters(regions, countries, period,
      this.excludeDeletedAccounts,
      filterOptions[ContractFilterOptionsModel.TopLevelCustomerName].selected.map(str => {
        const [h5Number, ...nameParts] = str.split(" ");
        const name = nameParts.join(" ");
        return { h5Number, name };
      }),
      filterOptions[ContractFilterOptionsModel.SecondLevelContractFilter].selected.map(str => {
        const [number, ...nameParts] = str.split(" ");
        const name = nameParts.join(" ");
        return { number, name };
      }),
      accountNumbers.map(accountNumber => ({ accountNumber, name: '' })),
        undefined, //template
        undefined, //translation
        this.getDateRangeForMixpanel(period ?? ''),
    );
    this.setSelectedAccountNos(accts);
    this.filtersSource.next(filters);
    //TODO: Make this Generic.
    const base: Partial<FilterSet> = {
      Period: this.buildPeriod(filters.period),
      Regions: filters.regions!,
      Countries: filters.countries!,
      Template:  ''
    };
    const specific: Partial<InstitutionalFilterSet> = {
      TopLevelContractGroups:filters.topLevelHierarchies?.map(v => v.h5Number!) ?? [],
      SecondLevelContracts: filters.h7Agreements?.map(v => v.number!) ?? [],
      AccountNumbers: filters.customers?.map(v => v.accountNumber!) ?? []
    };

    this.createFilterSet('InstitutionalFilterSet', base, specific);
    this.selectedFilters.next(await this.getSelectedFilters());
  }

  async confirmSelectionsForQSR(filterOptions: FilterOption[],accts: string[]) {
    let filters: Filters = new Filters();
    if (filterOptions == undefined) {
      this.filtersSource.next(filters);
      this.selectedFilters.next(await this.getSelectedFilters());
      return;
    }
    const regions = this.filterOptionService.mapRegionsToCodes(filterOptions[QSRFilterOptionsModel.Regions].selected);
    const countries = this.countriesService.mapCountryNamesToCodes(filterOptions[QSRFilterOptionsModel.Countries].selected);
    const period = this.filterOptionService.mapNameToCode(filterOptions[QSRFilterOptionsModel.DateRange].selected.toString(), FilterType.PERIOD);
    filters = new Filters(regions, countries, period,
      this.excludeDeletedAccounts,
      filterOptions[QSRFilterOptionsModel.TopLevelCustomerName].selected.map(str => {
        const [h5Number, ...nameParts] = str.split(" ");
        const name = nameParts.join(" ");
        return { h5Number, name };
      }),
      filterOptions[QSRFilterOptionsModel.SecondLevelContractFilter].selected.map(str => {
        const [number, ...nameParts] = str.split(" ");
        const name = nameParts.join(" ");
        return { number, name };
      }),

      accts.length ? accts.map(accountNumber => ({ accountNumber, name: '' })) :
      filterOptions[QSRFilterOptionsModel.CustomerNumberName].selected.map(str => {
        const [accountNumber, ...nameParts] = str.split(" ");
        const name = nameParts.join(" ");
        return { accountNumber, name };
      })
    );
    this.setSelectedAccountNos(accts);
    this.filtersSource.next(filters);
    //TODO: Make this Generic.
    const base: Partial<FilterSet> = {
      Period: this.buildPeriod(filters.period),
      Regions: filters.regions!,
      Countries: filters.countries!
    };
    const specific: Partial<QsrFilterSet> = {
      TopLevelContractGroups:filters.topLevelHierarchies?.map(v => v.h5Number!) ?? [],
      SecondLevelContracts: filters.h7Agreements?.map(v => v.number!) ?? [],
      AccountNumbers: filters.customers?.map(v => v.accountNumber!) ?? []
    };

    this.createFilterSet('QsrFilterSet', base, specific);
    this.selectedFilters.next(await this.getSelectedFilters());
  }

  async confirmSelectionsForFRS(filterOptions: FilterOption[],accts: string[]) {
    let filters: Filters = new Filters();
    if (filterOptions == undefined) {
      this.filtersSource.next(filters);
      this.selectedFilters.next(await this.getSelectedFilters());
      return;
    }
    const regions = this.filterOptionService.mapRegionsToCodes(filterOptions[FRSFilterOptionsModel.Regions].selected);
    const countries = this.countriesService.mapCountryNamesToCodes(filterOptions[FRSFilterOptionsModel.Countries].selected);
    const period = this.filterOptionService.mapNameToCode(filterOptions[FRSFilterOptionsModel.DateRange].selected.toString(), FilterType.PERIOD);
    filters = new Filters(regions, countries, period,
      this.excludeDeletedAccounts,
      filterOptions[FRSFilterOptionsModel.TopLevelCustomerName].selected.map(str => {
        const [h5Number, ...nameParts] = str.split(" ");
        const name = nameParts.join(" ");
        return { h5Number, name };
      }),
      filterOptions[FRSFilterOptionsModel.SecondLevelContractFilter].selected.map(str => {
        const [number, ...nameParts] = str.split(" ");
        const name = nameParts.join(" ");
        return { number, name };
      }),

      accts.length ? accts.map(accountNumber => ({ accountNumber, name: '' })) :
      filterOptions[FRSFilterOptionsModel.CustomerNumberName].selected.map(str => {
        const [accountNumber, ...nameParts] = str.split(" ");
        const name = nameParts.join(" ");
        return { accountNumber, name };
      })
    );
    this.setSelectedAccountNos(accts);
    this.filtersSource.next(filters);
       //TODO: Make this Generic.
    const base: Partial<FilterSet> = {
      Period: this.buildPeriod(filters.period),
      Regions: filters.regions!,
      Countries: filters.countries!
    };
    const specific: Partial<FrsFilterSet> = {
      TopLevelContractGroups:filters.topLevelHierarchies?.map(v => v.h5Number!) ?? [],
      SecondLevelContracts: filters.h7Agreements?.map(v => v.number!) ?? [],
      AccountNumbers: filters.customers?.map(v => v.accountNumber!) ?? []
    };

    this.createFilterSet('FrsFilterSet', base, specific);
    this.selectedFilters.next(await this.getSelectedFilters());
  }


  async setSavedSelectionFilter(filterOptions: ContractModel) {
    let selections : ContractModel = {
      division: this.selectedDivision,
      dataView: this.selectedDataView,
      topLevelContractGroup:  filterOptions.topLevelContractGroup ?? [],
      secondLevelContractFilter: filterOptions.secondLevelContractFilter ?? [],
      thirdLevelContractFilter: filterOptions.thirdLevelContractFilter ?? [],
      fourthLevelContractFilter: filterOptions.fourthLevelContractFilter ?? [],
      accts: filterOptions.accts ?? [],
      regions: filterOptions.regions ?? undefined,
      countries: filterOptions.countries ?? undefined,
      period: filterOptions.period ?? undefined,
      presentationType: filterOptions.presentationType ?? undefined,
      translation: filterOptions.translation ?? undefined,
      dateRange: this.getDateRangeForMixpanel(filterOptions.period ?? ''),
      excludeDeletedAccounts: this.excludeDeletedAccounts
    }

    // QSR and Institutional Contract stil saved as ctrAcctModel as in few API's the body is not updated to ContractModel
    let ctrAcctSelections : Filters = {
      regions: filterOptions.regions ?? undefined,
      countries: filterOptions.countries ?? undefined,
      period: filterOptions.period ?? undefined,
      topLevelHierarchies: filterOptions?.topLevelContractGroup?.map(v => { return { h5Number: v, name: "" }}) ?? [],
      h7Agreements: filterOptions?.secondLevelContractFilter?.map(v => { return { number: v, name: "" }}) ?? [],
      customers: filterOptions?.accts?.map(v => { return { accountNumber: v, name: "" }}) ?? [],
      presentationType: filterOptions.presentationType ?? undefined,
      translation: filterOptions.translation ?? undefined,
      dateRange: this.getDateRangeForMixpanel(filterOptions.period ?? ''),
      excludeDeletedAccounts: this.excludeDeletedAccounts
    }

    const h5h7Divisions = ['QSR', 'FRS'];
    if(this.selectedDivision == "Distributor"){
      this.distributorFilterSource.next(selections)
    }else if((this.selectedDivision == "Institutional" && this.selectedDataView == "Contract") || h5h7Divisions.includes(this.selectedDivision)){
      this.filtersSource.next(ctrAcctSelections);
    }
    this.selectedFilters.next(await this.getSelectedFilters());
  }

  async confirmSelectionsForDistributorView(filterOptions: FilterOption[], accts: string[]) {
    if (filterOptions == undefined) {
      this.distributorFilterSource.next(undefined);
      this.selectedFilters.next(await this.getSelectedFilters());
      return;
    }
    const period = this.filterOptionService.mapNameToCode(filterOptions[DistributorFilterModel.Period].selected.toString(), FilterType.PERIOD);
    let selections : ContractModel = {
      division: this.selectedDivision,
      dataView: this.selectedDataView,
      topLevelContractGroup: filterOptions[DistributorFilterModel.TopLevelContractGroup].selected ?? [], //.map(str => str.split(" ")[0]) ?? [],
      secondLevelContractFilter: filterOptions[DistributorFilterModel.SecondLevelContractFilter].selected ?? [],
      thirdLevelContractFilter: undefined,
      fourthLevelContractFilter: undefined,
      accts: (accts.length > 0 ? accts : filterOptions[DistributorFilterModel.Accts].selected.map(str => str.split(" ")[0])) ?? [],
      regions: [],
      countries: [],
      period: period,
      presentationType: undefined,
      translation: undefined,
      dateRange: this.getDateRangeForMixpanel(period ?? ''),
      excludeDeletedAccounts: this.excludeDeletedAccounts // For Distributor the value will be false and is not editable in the UI
    }
    this.distributorFilterSource.next(selections);
              //TODO: Make this Generic.
    const base: Partial<FilterSet> = {
      Period: this.buildPeriod(selections.period),
      Regions: selections.regions!,
      Countries: selections.countries!
    };
    const specific: Partial<DistributorFilterSet> = {
      PartnerGroups: selections.topLevelContractGroup ?? [],
      DistributorGroups: selections.secondLevelContractFilter ?? [],
      DistributorNumbers: selections.accts ?? []
    };

    this.createFilterSet('DistributorFilterSet', base, specific);
    this.selectedFilters.next(await this.getSelectedFilters());
  }

  private async getSelectedFilters(): Promise<ContractModel | undefined> {

    // QSR and Institutional Contract stil saved as ctrAcctModel as in few API's the body is not updated to ContractModel
    if (this.selectedDivision == "Institutional" && this.selectedDataView == "Contract" || this.selectedDivision == "QSR" || this.selectedDivision === 'Pest' || this.selectedDivision == "FRS") {
      let filters = this.filtersSource.getValue();
      if (filters!=undefined && filters.regions != undefined && filters.countries != undefined) {
        let contractModel: ContractModel = {
          division: this.selectedDivision,
          dataView: this.selectedDataView,
          topLevelContractGroup: filters.topLevelHierarchies!.map(v => v.h5Number!),
          secondLevelContractFilter: filters.h7Agreements!.map(v => v.number!),
          thirdLevelContractFilter: filters.thirdLevelContractFilter!,
          fourthLevelContractFilter: undefined,
          accts: filters.customers!.map(v => v.accountNumber!),
          regions: filters.regions,
          countries: filters.countries,
          period: filters.period,
          presentationType: filters.presentationType,
          translation: filters.translation,
          dateRange: this.getDateRangeForMixpanel(filters.period ?? ''),
          oneCustomerName: this.oneCustomerName,
          oneCustomerFilters: this.oneCustomerFilters?.value,
          excludeDeletedAccounts: this.excludeDeletedAccounts
        }

        // Infer H5 from H7 when user selected H7 without selecting H5.
        // This is done asynchronously to avoid blocking navigation — inferredH5 is for analytics only.
        if (
          (!contractModel.topLevelContractGroup || contractModel.topLevelContractGroup.length === 0) &&
          contractModel.secondLevelContractFilter &&
          contractModel.secondLevelContractFilter.length > 0
        ) {
          //For FRS and QSR divisions, dataView is the same as division name
          const routeDataView = this.selectedDataView || this.selectedDivision;
          this.filterRequests.getH5ForH7Numbers(
            contractModel.regions ?? [],
            contractModel.countries ?? [],
            routeDataView,
            contractModel.secondLevelContractFilter,
            this.selectedDivision
          ).then(h5Results => {
            const inferredH5 = h5Results.map(str => str.split(' ')[0]);
            const current = this.selectedFilters.value;
            if (current) {
              this.selectedFilters.next({ ...current, inferredH5 });
            }
          }).catch(e => {
            // Silently fail -- inferred H5 is for analytics only, should not block filter confirmation
            console.warn('Failed to infer H5 from H7 for Mixpanel:', e);
          });
        }

        return contractModel;
      }
    }
    else if (this.selectedDivision == "Distributor") {
      let filters = this.distributorFilterSource.getValue();
      if (filters != undefined && (filters.topLevelContractGroup != undefined || filters.secondLevelContractFilter != undefined || (filters.accts != undefined && filters.accts.length>0))) {
        return filters;
      }
    }
    return undefined;
  }

  async preSelectFilters(filterOptions : any){
    var selectedFilters = await this.getSelectedFilters();
    filterOptions = cloneDeep(filterOptions);
    if(this.selectedDivision == "Distributor"){
      filterOptions[DistributorFilterModel.TopLevelContractGroup].selected = selectedFilters?.topLevelContractGroup ?? [];
      filterOptions[DistributorFilterModel.SecondLevelContractFilter].selected = selectedFilters?.secondLevelContractFilter ?? [];
      filterOptions[DistributorFilterModel.Accts].selected = selectedFilters?.accts ?? [];
      this.accts = selectedFilters?.accts ?? [];
    }else if(this.selectedDivision == "Institutional" && this.selectedDataView == "Contract"){
      filterOptions[ContractFilterOptionsModel.Regions].selected = selectedFilters?.regions ?? "";
      filterOptions[ContractFilterOptionsModel.Countries].selected = selectedFilters?.countries ?? [];
      filterOptions[ContractFilterOptionsModel.TopLevelCustomerName].selected = selectedFilters?.topLevelContractGroup ?? [];
      filterOptions[ContractFilterOptionsModel.SecondLevelContractFilter].selected = selectedFilters?.secondLevelContractFilter ?? [];
      filterOptions[ContractFilterOptionsModel.CustomerNumberName].selected = selectedFilters?.accts ?? [];
    }else if(this.selectedDivision == "QSR"){
      filterOptions[QSRFilterOptionsModel.Regions].selected = selectedFilters?.regions ?? "";
      filterOptions[QSRFilterOptionsModel.Countries].selected = selectedFilters?.countries ?? [];
      filterOptions[QSRFilterOptionsModel.TopLevelCustomerName].selected = selectedFilters?.topLevelContractGroup ?? [];
      filterOptions[QSRFilterOptionsModel.SecondLevelContractFilter].selected = selectedFilters?.secondLevelContractFilter ?? [];
      filterOptions[QSRFilterOptionsModel.CustomerNumberName].selected = selectedFilters?.accts ?? [];
    }
    else if(this.selectedDivision == "FRS"){
      filterOptions[FRSFilterOptionsModel.Regions].selected = selectedFilters?.regions ?? "";
      filterOptions[FRSFilterOptionsModel.Countries].selected = selectedFilters?.countries ?? [];
      filterOptions[FRSFilterOptionsModel.TopLevelCustomerName].selected = selectedFilters?.topLevelContractGroup ?? [];
      filterOptions[FRSFilterOptionsModel.SecondLevelContractFilter].selected = selectedFilters?.secondLevelContractFilter ?? [];
      filterOptions[FRSFilterOptionsModel.CustomerNumberName].selected = selectedFilters?.accts ?? [];
    }

    // show all options
    forEach(filterOptions, (option) => {
        option.hidden = false;

    });
    return filterOptions;
  }

  public setSelectedAccountNos(accts: string []): void {
      this.selectedAccountNosSubject.next(accts);
  }

  public getSelectedAccountNos(): string [] {
    return this.selectedAccountNosSubject.getValue();
  }

  public getBenchmarkOptions(): string [] {
    return this.benchMarkOptionsSubject.getValue();
  }

  public setBenchmarkOptions(benchMarkOptions: string []): void {
      this.benchMarkOptionsSubject.next(benchMarkOptions);
  }

  //#region Custom Date Range
  public getMinimumDate(): Date {
    return this.minimumDate;
  }
  public getMaximumDate(): Date {
    return this.maximumDate;
  }
  public setCustomDateRangeStart(startDate:Moment){
    const formattedStartDate = startDate.format('YYYY-MM');
    const endDate = this.customDateRange.getValue().endDate;
    this.customDateRange.next({startDate: {moment:startDate, formatted:formattedStartDate}, endDate: endDate});
  }

  public setCustomDateRangeStartUsingString(startDate:string){ //assumes startDate is in YYYY-MM format
    let startDateMoment = _moment.default();
    startDateMoment.year(Number(startDate.substring(0,4)));
    startDateMoment.month(Number(startDate.substring(5,7))-1); //months are zero indexed for the moment object, so subtract 1

    const endDate = this.customDateRange.getValue().endDate;
    this.customDateRange.next({startDate: {moment:startDateMoment, formatted:startDate.substring(0,7)}, endDate: endDate});
  }

  public setCustomDateRangeEnd(endDate:Moment){
    const formattedEndDate = endDate.format('YYYY-MM');
    const startDate = this.customDateRange.getValue().startDate;
    this.customDateRange.next({startDate: startDate, endDate: {moment:endDate, formatted:formattedEndDate}});
  }

  public setCustomDateRangeEndUsingString(endDate:string){ //assumes endDate is in YYYY-MM format
    let endDateMoment = _moment.default();
    endDateMoment.year(Number(endDate.substring(0,4)));
    endDateMoment.month(Number(endDate.substring(5,7))-1); //months are zero indexed for the moment object, so subtract 1

    const startDate = this.customDateRange.getValue().startDate;
    this.customDateRange.next({startDate: startDate, endDate: {moment:endDateMoment, formatted:endDate.substring(0,7)}});
  }

  //Verify that the custom date range is within the past 24 months
  public isDateRangeWithinLimits(): boolean{
    if(this.customDateRange.getValue().startDate.moment.isBetween(this.minimumDate, this.maximumDate, 'month', '[]')
       && this.customDateRange.getValue().endDate.moment.isBetween(this.minimumDate, this.maximumDate, 'month', '[]')){
      return true
    }
    return false;
  }

  // Returns a URL with the custom date range appended if the period is 'Custom'
  public appendCustomDateRangeToUrl(url: string): string {
    if (this.selectedFilters.value?.period === 'Custom') {
      let formattedRange : string = `${this.customDateRange.value.startDate.formatted}:${this.customDateRange.value.endDate.formatted}`
      return `${url}&dateRange=${formattedRange}`;
    }
    return url;
  }

  // Check if a previous period is available based on the current selected period.
  // For example, if the current month is July and the selected period is R3, last 3 months,
  // the current period would be April-June and the previous period would be January-March.
  public IsPreviousPeriodAvailable():boolean {
    //EBR only has 3 years of data, so a previous period from R24 is invalid since it requires 4 years
    if(this.selectedFilters.value?.period === "R24"){
      return false;
    }
    else if (this.selectedFilters.value?.period === "Custom"){
      // Get the number of months within the selected custom range (inclusive of start and end months)
      const startDate = this.customDateRange.getValue().startDate.moment;
      const endDate = this.customDateRange.getValue().endDate.moment;
      const numberOfMonths = (endDate.get('year') - startDate.get('year')) * 12 + endDate.get('month') - startDate.get('month') + 1;

      // Create a copy of the start date to avoid mutating the original
      let startCopy = _moment.default(startDate);
      // Get the previous period's start Date. Moment.subtract() mutates the original and returns a new Moment object
      const previousStartDate = startCopy.subtract(numberOfMonths, 'months');

      if (previousStartDate.isBefore(this.minimumDate)){
        return false;
      }
      else{
        return true;
      }
    }
    // All other pre-set period options have data available for previous periods
    return true;
  }

  //#endregion

  async confirmSelectionsForPest(filterOptions: FilterOption[],serviceIds: string[]){
    let filters: Filters = new Filters();
    if (filterOptions == undefined) {
      this.filtersSource.next(filters);
      this.selectedFilters.next(await this.getSelectedFilters());
      return;
    }
    const regions = this.filterOptionService.mapRegionsToCodes(filterOptions[PestFilterModel.Regions].selected);
    const countries = this.countriesService.mapCountryNamesToCodes(filterOptions[PestFilterModel.Countries].selected);
    const period = this.filterOptionService.mapNameToCode(filterOptions[PestFilterModel.DateRange].selected.toString(), FilterType.PERIOD);
    const template = '';
    const templateTranslated = '';
    const dateRange = '';
    filters = new Filters(regions, countries, period,
      this.excludeDeletedAccounts, // For Distributor the value will be false and is not editable in the UI until business decision is made
      filterOptions[PestFilterModel.TopLevelCustomerName].selected.map(str => {
        const [h5Number, ...nameParts] = str.split(" ");
        const name = nameParts.join(" ");
        return { h5Number, name };
      }),
      filterOptions[PestFilterModel.SecondLevelContractFilter].selected.map(str => {
        const [number, ...nameParts] = str.split(" ");
        const name = nameParts.join(" ");
        return { number, name };
      }),

      serviceIds.length ? serviceIds.map(serviceId => ({ accountNumber: serviceId, name: '' })) :
      filterOptions[PestFilterModel.CustomerNumberName].selected.map(str => {
        const [accountNumber, ...nameParts] = str.split(" ");
        const name = nameParts.join(" ");
        return { accountNumber, name };
      }),
      template,
      templateTranslated,
      dateRange,
    );
    this.setSelectedAccountNos(serviceIds);
    this.filtersSource.next(filters);
           //TODO: Make this Generic.
    const base: Partial<FilterSet> = {
      Period: this.buildPeriod(filters.period),
      Regions: filters.regions!,
      Countries: filters.countries!
    };
    const specific: Partial<PestFilterSet> = {
      TopLevelContractGroups:filters.topLevelHierarchies?.map(v => v.h5Number!) ?? [],
      SecondLevelContracts: filters.h7Agreements?.map(v => v.number!) ?? [],
      AccountNumbers: filters.customers?.map(v => v.accountNumber!) ?? []
    };

    this.createFilterSet('PestFilterSet', base, specific);

    this.selectedFilters.next(await this.getSelectedFilters());
  }
  //#end


  createFilterSet<T extends FilterSet>(
    type: string,
    base: Partial<FilterSet>,
    specific: Partial<T>
  ): T {
    const filterSet = {
      $type: type,
      Period: base.Period,
      Regions: base.Regions,
      Countries: base.Countries,
      Template: base.Template ?? '',
      ExcludeDeletedAccounts: base.ExcludeDeletedAccounts ?? this.excludeDeletedAccounts,
      ...specific
    } as T;
    this.filterSet.next(filterSet);
    return filterSet;
  }

  /**
   * Resolves the concrete [start, end] month range for a selected period, mirroring the API's
   * RollingTimePeriod -> DateRange conversion. The window always ends on the last complete month
   * (the previous month, since data lags one month — see maximumDate); the start is derived from
   * the preset kind. 'Custom' uses the user-picked customDateRange as-is.
   *
   * Returned moments are clones set to the first of their respective months, so callers can format
   * or mutate them freely.
   */
  public getSelectedPeriodRange(periodType?: string): { start: Moment; end: Moment } {
    const period = periodType ?? this.selectedFilters.value?.period;

    if (period === 'Custom') {
      const customRange = this.customDateRange.getValue();
      return {
        start: customRange.startDate.moment.clone().startOf('month'),
        end: customRange.endDate.moment.clone().startOf('month'),
      };
    }

    // End of the window is the last complete month (maximumDate is the last day of the prior month).
    const end = _moment.default(this.maximumDate).startOf('month');
    const start = end.clone();

    switch (period) {
      case 'Prior Month':
        // Single month: start stays equal to end.
        break;
      case 'R3':
        start.subtract(2, 'months');
        break;
      case 'R6':
        start.subtract(5, 'months');
        break;
      case 'R12':
        start.subtract(11, 'months');
        break;
      case 'R24':
        start.subtract(23, 'months');
        break;
      case 'YTD':
        // January of the end's year.
        start.month(0);
        break;
      default:
        // Matches the API default (R12 window).
        start.subtract(11, 'months');
        break;
    }

    return { start, end };
  }

  buildPeriod(periodType?: string): TimePeriod {
    if (periodType === 'Custom') {
        const startDate = this.customDateRange.getValue().startDate.moment;
        const endDate = this.customDateRange.getValue().endDate.moment;
      return {
        $type: 'FixedTimePeriod',
        DateRange: {
          // Note: Moment.js uses 0-based months (0=Jan), but .NET API expects 1-based (1=Jan)
          // Convert 0-based to 1-based month
          Start: { Year: startDate.year(), Month: startDate.month() + 1},
        End: { Year: endDate.year(), Month: endDate.month() + 1}
        }
      };
    }

    let periodKind = periodType ?? 'Undefined';
    if(periodType == 'Prior Month'){
      periodKind = "PriorMonth";
    }
    return {
      $type: 'RollingTimePeriod',
      Kind: periodKind
    };
  }

}
